import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FileUpload from '../../components/common/FileUpload';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Download, FileText, BookOpen, AlertCircle, Plus, Trash2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';

const ManagementGuide = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [docForm, setDocForm] = useState({ title: '', category: 'Governance & Policies', file_url: '', file_type: 'PDF', description: '' });
  const [savingDoc, setSavingDoc] = useState(false);

  const categoryColors = {
    'Governance & Policies': 'bg-blue-500',
    'Financial Management': 'bg-green-500',
    'Meeting Procedures': 'bg-purple-500',
    'Member Relations': 'bg-orange-500',
    'Strategic Planning': 'bg-red-500',
    'Legal & Compliance': 'bg-yellow-500',
    'General': 'bg-gray-500',
  };

  const categoryIcons = {
    'Governance & Policies': BookOpen,
    'Financial Management': FileText,
    'Meeting Procedures': FileText,
    'Member Relations': FileText,
    'Strategic Planning': FileText,
    'Legal & Compliance': AlertCircle,
    'General': FileText,
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/executive/documents`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({ title: 'Error', description: 'Failed to load documents', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDoc = async () => {
    if (!docForm.title || !docForm.file_url) {
      toast({ title: 'Missing fields', description: 'Title and file URL are required', variant: 'destructive' });
      return;
    }
    setSavingDoc(true);
    try {
      const response = await axios.post(`${backendUrl}/api/executive/documents`, docForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocuments(prev => [response.data, ...prev]);
      setDocForm({ title: '', category: 'Governance & Policies', file_url: '', file_type: 'PDF', description: '' });
      setShowAddDialog(false);
      toast({ title: 'Document Added', description: 'Document has been uploaded' });
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to add document', variant: 'destructive' });
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Archive this document? It can be restored later.')) return;
    try {
      await axios.delete(`${backendUrl}/api/executive/documents/${docId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast({ title: 'Archived', description: 'Document archived' });
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to delete document', variant: 'destructive' });
    }
  };

  // Group documents by category
  const guideCategories = Object.keys(categoryColors).map((catName, idx) => ({
    id: idx + 1,
    title: catName,
    icon: categoryIcons[catName] || FileText,
    color: categoryColors[catName],
    documents: documents.filter(d => d.category === catName)
  })).filter(cat => cat.documents.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              MANAGEMENT GUIDE
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
            <p className="mt-4 text-lg text-white/90">
              Essential documents and resources for executive committee members
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        {/* Info Banner */}
        <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg max-w-5xl mx-auto">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <BookOpen className="h-6 w-6 text-blue-500" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">Executive Resource Library</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Access comprehensive guides, policies, and templates to support your executive duties. 
                  All documents are regularly updated and approved by the executive committee.
                </p>
              </div>
            </div>
            <Button className="bg-[#2c3e7d] hover:bg-[#1e2a5a] ml-4" onClick={() => setShowAddDialog(true)}>
              <Plus size={18} className="mr-2" />
              Add Document
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          </div>
        ) : guideCategories.length === 0 ? (
          <div className="max-w-5xl mx-auto text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No documents uploaded yet. Click "Add Document" to upload the first one.</p>
          </div>
        ) : (
          /* Document Categories */
          <div className="max-w-7xl mx-auto space-y-6">
            {guideCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Card key={category.id} className="overflow-hidden">
                  <CardHeader className={`${category.color} text-white`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} />
                      </div>
                      <CardTitle className="text-xl">{category.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {category.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                              <FileText size={20} className="text-gray-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{doc.title}</p>
                              <p className="text-xs text-gray-500">{doc.file_type || 'PDF'}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(doc.file_url, '_blank')}
                              className="hover:bg-[#2c3e7d] hover:text-white"
                            >
                              <Download size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteDoc(doc.id)}
                              className="hover:bg-red-600 hover:text-white"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 max-w-5xl mx-auto">
          <Card className="bg-gradient-to-r from-[#2c3e7d] to-[#1e2a5a] text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">Need Additional Resources?</h3>
              <p className="mb-6 text-white/90">
                Can't find what you're looking for? Contact the executive secretary or submit a resource request.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Secretary
                </Button>
                <Button
                  variant="outline"
                  className="bg-transparent border-white text-white hover:bg-white hover:text-[#2c3e7d]"
                  onClick={() => window.location.href = '/executive/meeting-request'}
                >
                  Request Meeting
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Document Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Executive Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={docForm.title}
                onChange={(e) => setDocForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Document title..."
              />
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={docForm.category}
                onChange={(e) => setDocForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.keys(categoryColors).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <FileUpload
                folder="management-guide"
                label="Document File *"
                currentUrl={docForm.file_url}
                onUpload={(url, filename) => {
                  const ext = filename ? filename.split('.').pop().toUpperCase() : 'PDF';
                  setDocForm(prev => ({ ...prev, file_url: url, file_type: ext }));
                }}
                onClear={() => setDocForm(prev => ({ ...prev, file_url: '', file_type: '' }))}
              />
            </div>
            <div>
              <Label>File Type</Label>
              <Input
                value={docForm.file_type}
                onChange={(e) => setDocForm(prev => ({ ...prev, file_type: e.target.value }))}
                placeholder="PDF, DOCX, etc."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={docForm.description}
                onChange={(e) => setDocForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDoc} className="bg-[#2c3e7d]" disabled={savingDoc}>
              {savingDoc ? 'Adding...' : 'Add Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementGuide;